import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useData } from "@/context/DataContext";
import { toast } from "sonner";
import { GraduationCap, CheckCircle2, XCircle, MinusCircle, Building2, ChevronRight, Info, Camera, Sparkles, Ban, AlertTriangle, Search, Filter, X as XIcon } from "lucide-react";
import { MonthMultiSelect } from "@/components/MonthMultiSelect";
import { Skeleton } from "@/components/Skeleton";
import EmptyState from "@/components/EmptyState";

const STATUSES = ["unmarked", "present", "absent", "cancelled"];
const toLocalYmd = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
};
const todayYmd = () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return toLocalYmd(d);
};
const formatDay = (ymd) => {
    if (!ymd) return "—";
    const [y, m, d] = ymd.split("-");
    return new Date(`${y}-${m}-${d}T00:00:00`).toLocaleDateString("en-IN", { day: "2-digit" });
};
const formatMonth = (ym) => {
    const [y, m] = ym.split("-");
    return new Date(`${y}-${m}-01T00:00:00`).toLocaleDateString("en-IN", { month: "short", year: "2-digit" });
};
/** All Wednesdays strictly inside a given year+month (0-indexed month). */
const wednesdaysInMonth = (year, month) => {
    const out = [];
    const d = new Date(year, month, 1);
    // Advance to the first Wednesday of the month
    const offset = (3 - d.getDay() + 7) % 7;
    d.setDate(1 + offset);
    while (d.getMonth() === month) {
        out.push(toLocalYmd(d));
        d.setDate(d.getDate() + 7);
    }
    return out;
};
/** Build a window of full months: `monthsBack` complete months behind + current + `monthsForward` complete months ahead. */
const buildMonthlyWednesdayWindow = ({ monthsBack = 2, monthsForward = 1 } = {}) => {
    const now = new Date();
    const out = [];
    for (let i = -monthsBack; i <= monthsForward; i++) {
        const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
        out.push(...wednesdaysInMonth(d.getFullYear(), d.getMonth()));
    }
    return out;
};
/** Group Wednesdays by "YYYY-MM" preserving chronological order. */
const groupWeeksByMonth = (weeks) => {
    const groups = [];
    for (const w of weeks) {
        const ym = w.slice(0, 7);
        const last = groups[groups.length - 1];
        if (!last || last.ym !== ym) groups.push({ ym, weeks: [w] });
        else last.weeks.push(w);
    }
    return groups;
};

const KnoAttendance = () => {
    const { user } = useAuth();
    const { users, depts } = useData();
    const navigate = useNavigate();
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    // Evidence drill-down popover: { userId, sessionDate, userName, data, loading, x, y }
    const [evidence, setEvidence] = useState(null);
    const openEvidence = async (u, wed) => {
        setEvidence({ userId: u.user_id, userName: u.name, sessionDate: wed, loading: true, data: null });
        try {
            const r = await api.get("/kno/evidence", { params: { user_id: u.user_id, session_date: wed } });
            setEvidence((prev) => (prev && prev.userId === u.user_id && prev.sessionDate === wed ? { ...prev, loading: false, data: r.data } : prev));
        } catch (e) {
            setEvidence((prev) => (prev && prev.userId === u.user_id && prev.sessionDate === wed ? { ...prev, loading: false, data: null, error: e?.response?.data?.detail || "Couldn't load evidence" } : prev));
        }
    };

    const canMark = user?.role === "super_admin" || user?.role === "coordinator";
    const weeks = useMemo(() => buildMonthlyWednesdayWindow({ monthsBack: 2, monthsForward: 1 }), []);
    const allMonthGroups = useMemo(() => groupWeeksByMonth(weeks), [weeks]);
    const [search, setSearch] = useState("");
    const [deptFilter, setDeptFilter] = useState("all");
    const [userFilter, setUserFilter] = useState("all");
    const [monthSel, setMonthSel] = useState([]);
    const [criticalOnly, setCriticalOnly] = useState(false);
    const monthGroups = useMemo(
        () => (monthSel.length ? allMonthGroups.filter((g) => monthSel.includes(g.ym)) : allMonthGroups),
        [allMonthGroups, monthSel],
    );
    const shownWeeks = useMemo(() => monthGroups.flatMap((g) => g.weeks), [monthGroups]);
    // First Wednesday of every month after the first gets a left divider.
    const monthStartWeeks = useMemo(() => new Set(monthGroups.slice(1).map((g) => g.weeks[0])), [monthGroups]);
    const today = todayYmd();

    const visibleUsers = useMemo(() => {
        if (!user) return [];
        if (user.role === "super_admin" || user.role === "coordinator") {
            return (users || []).filter((u) => u.active);
        }
        if (user.role === "manager") return (users || []).filter((u) => u.manager_id === user.user_id || u.user_id === user.user_id);
        return (users || []).filter((u) => u.user_id === user.user_id);
    }, [user, users]);

    const deptById = useMemo(() => Object.fromEntries((depts || []).map((d) => [d.id, d])), [depts]);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/kno");
            setRecords(r.data || []);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    useEffect(() => {
    }, []);

    const isWednesdayIST = useMemo(() => {
        const ist = new Date(Date.now() + 330 * 60 * 1000);
        return ist.getUTCDay() === 3;
    }, []);

    // Build lookup: [user_id][session_date] -> record
    const map = useMemo(() => {
        const m = new Map();
        for (const r of records) {
            if (!m.has(r.user_id)) m.set(r.user_id, {});
            m.get(r.user_id)[r.session_date] = r;
        }
        return m;
    }, [records]);

    // Missed (absent) sessions per user per month — >1 in a month is a critical instance.
    const missedOf = (uid, ym) => {
        const row = map.get(uid) || {};
        return Object.entries(row).filter(([d, r]) => d.startsWith(ym) && r.status === "absent").length;
    };
    const criticalIds = useMemo(() => {
        const set = new Set();
        for (const u of visibleUsers) if (monthGroups.some((g) => missedOf(u.user_id, g.ym) > 1)) set.add(u.user_id);
        return set;
    // eslint-disable-next-line
    }, [visibleUsers, monthGroups, map]);

    const filteredUsers = useMemo(() => {
        const q = search.trim().toLowerCase();
        return visibleUsers.filter((u) => {
            if (deptFilter !== "all" && u.department_id !== deptFilter) return false;
            if (userFilter !== "all" && u.user_id !== userFilter) return false;
            if (criticalOnly && !criticalIds.has(u.user_id)) return false;
            if (q && !`${u.name || ""} ${u.email || ""} ${deptById[u.department_id]?.name || ""}`.toLowerCase().includes(q)) return false;
            return true;
        });
    }, [visibleUsers, deptFilter, userFilter, criticalOnly, criticalIds, search, deptById]);

    // Group visible users by department
    const grouped = useMemo(() => {
        const g = new Map();
        for (const u of filteredUsers) {
            const key = deptById[u.department_id]?.name || "— Unassigned —";
            if (!g.has(key)) g.set(key, []);
            g.get(key).push(u);
        }
        const arr = Array.from(g.entries()).map(([dept, list]) => ({
            dept,
            list: [...list].sort((a, b) => a.name.localeCompare(b.name)),
        }));
        arr.sort((a, b) => a.dept.localeCompare(b.dept));
        return arr;
    }, [filteredUsers, deptById]);

    // Click cycle: unmarked -> present -> absent -> cancelled -> unmarked (delete)
    const cycleStatus = async (u, wed, currentStatus) => {
        if (!canMark) return;
        if (saving) return;
        if (wed > today) {
            toast.info("This Wednesday hasn't happened yet.");
            return;
        }
        setSaving(true);
        try {
            if (!currentStatus || currentStatus === "unmarked") {
                // set present
                await api.post("/kno/mark", { user_id: u.user_id, session_date: wed, status: "present" });
                // optimistic update
                setRecords((rs) => [...rs.filter((r) => !(r.user_id === u.user_id && r.session_date === wed)), {
                    user_id: u.user_id, employee_name: u.name, department_id: u.department_id,
                    department_name: deptById[u.department_id]?.name || "",
                    session_date: wed, status: "present", marked_at: new Date().toISOString(), marked_by: user.email,
                    id: `tmp-${u.user_id}-${wed}`,
                }]);
            } else if (currentStatus === "present") {
                await api.post("/kno/mark", { user_id: u.user_id, session_date: wed, status: "absent" });
                setRecords((rs) => rs.map((r) => (r.user_id === u.user_id && r.session_date === wed ? { ...r, status: "absent" } : r)));
            } else if (currentStatus === "absent") {
                await api.post("/kno/mark", { user_id: u.user_id, session_date: wed, status: "cancelled" });
                setRecords((rs) => rs.map((r) => (r.user_id === u.user_id && r.session_date === wed ? { ...r, status: "cancelled" } : r)));
            } else if (currentStatus === "cancelled") {
                // delete to unmark
                const rec = map.get(u.user_id)?.[wed];
                if (rec?.id && !rec.id.startsWith("tmp-")) {
                    await api.delete(`/kno/${rec.id}`);
                }
                setRecords((rs) => rs.filter((r) => !(r.user_id === u.user_id && r.session_date === wed)));
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to update");
            await load();
        } finally {
            setSaving(false);
        }
    };

    // Summary stats across visible records. Cancelled is tracked separately
    // and excluded from the attendance-rate denominator so a cancelled
    // session never hurts your rate.
    const stats = useMemo(() => {
        let present = 0, absent = 0, cancelled = 0;
        const ids = new Set(filteredUsers.map((u) => u.user_id));
        const wk = new Set(shownWeeks);
        for (const r of records) {
            if (!ids.has(r.user_id) || !wk.has(r.session_date)) continue;
            if (r.status === "present") present++;
            else if (r.status === "absent") absent++;
            else if (r.status === "cancelled") cancelled++;
        }
        const denom = present + absent;
        const total = present + absent + cancelled;
        const rate = denom ? Math.round((present / denom) * 100) : null;
        const critical = filteredUsers.filter((u) => criticalIds.has(u.user_id)).length;
        return { present, absent, cancelled, total, rate, critical };
    }, [records, filteredUsers, shownWeeks, criticalIds]);
    const showPeopleFilters = visibleUsers.length > 1;

    return (
        <div className="space-y-6 sm:space-y-8" data-testid="kno-page">
            <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Weekly knowledge session</div>
                <h1 className="font-heading text-3xl sm:text-5xl font-bold text-slate-900 tracking-tight mt-1 inline-flex items-center gap-2 sm:gap-3">
                    <GraduationCap className="h-7 w-7 sm:h-8 sm:w-8 text-[#2EB4AC]" />
                    KNO Attendance
                </h1>
                <p className="text-sm sm:text-base text-slate-500 mt-2">
                    Held every Wednesday. {canMark ? "Click any past cell to cycle Present → Absent → Cancelled → Unmarked. Cancelled sessions don't affect attendance rate." : "View-only — your coordinator marks weekly attendance."}
                </p>
            </div>

            <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-4 flex flex-wrap items-center gap-3" data-testid="kno-filters">
                <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">
                    <Filter className="h-4 w-4" /> Filters
                </div>
                {showPeopleFilters && (
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search employee or department…"
                            data-testid="kno-search-input"
                            className="h-10 pl-9 pr-8 w-64 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:border-[#2EB4AC] focus:ring-2 focus:ring-teal-100"
                        />
                        {search && (
                            <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700" aria-label="Clear search">
                                <XIcon className="h-3.5 w-3.5" />
                            </button>
                        )}
                    </div>
                )}
                <MonthMultiSelect value={monthSel} onChange={setMonthSel} months={allMonthGroups.map((g) => g.ym)} format={formatMonth} testid="kno-month-filter" optionPrefix="kno-month-option" />
                {showPeopleFilters && (
                    <select
                        value={deptFilter}
                        onChange={(e) => { setDeptFilter(e.target.value); setUserFilter("all"); }}
                        data-testid="kno-dept-filter"
                        className="h-10 px-3 rounded-lg border border-slate-200 bg-white text-sm text-slate-700 focus:outline-none focus:border-[#2EB4AC]"
                    >
                        <option value="all">All departments</option>
                        {[...(depts || [])].sort((a, b) => a.name.localeCompare(b.name)).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                )}
                {showPeopleFilters && (
                    <select
                        value={userFilter}
                        onChange={(e) => setUserFilter(e.target.value)}
                        data-testid="kno-user-filter"
                        className="h-10 px-3 rounded-lg border border-slate-200 bg-white text-sm text-slate-700 focus:outline-none focus:border-[#2EB4AC]"
                    >
                        <option value="all">All team members</option>
                        {visibleUsers.filter((u) => deptFilter === "all" || u.department_id === deptFilter).sort((a, b) => a.name.localeCompare(b.name)).map((u) => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
                    </select>
                )}
                {showPeopleFilters && (
                    <button
                        onClick={() => setCriticalOnly((v) => !v)}
                        data-testid="kno-critical-only"
                        className={`inline-flex items-center gap-1.5 h-10 px-3 rounded-lg border text-sm font-medium transition-colors ${criticalOnly ? "bg-rose-50 border-rose-300 text-rose-700" : "bg-white border-slate-200 text-slate-700 hover:border-rose-300 hover:text-rose-700"}`}
                    >
                        <AlertTriangle className="h-4 w-4" /> Critical only
                        <span className={`inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded-full text-[10px] font-bold ${criticalOnly ? "bg-rose-600 text-white" : "bg-rose-50 text-rose-700"}`}>{criticalIds.size}</span>
                    </button>
                )}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-6 gap-3 sm:gap-4">
                <StatTile icon={CheckCircle2} label="Present" value={stats.present} color="bg-green-100 text-green-700" />
                <StatTile icon={XCircle} label="Absent" value={stats.absent} color="bg-red-100 text-red-700" />
                <StatTile icon={Ban} label="Cancelled" value={stats.cancelled} color="bg-slate-100 text-slate-600" />
                <StatTile icon={Info} label="Marked" value={stats.total} color="bg-slate-100 text-slate-700" />
                <StatTile icon={GraduationCap} label="Rate" value={stats.rate != null ? `${stats.rate}%` : "—"} color="bg-teal-50 text-[#25908A]" />
                <StatTile icon={AlertTriangle} label="Critical" value={stats.critical} color="bg-rose-100 text-rose-700" testid="kno-critical-tile" />
            </div>

            <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
                    <h3 className="font-heading text-lg font-semibold text-slate-900 inline-flex items-center gap-2">
                        <GraduationCap className="h-4 w-4 text-[#2EB4AC]" />
                        Marking grid
                        <span className="text-xs font-normal text-slate-500">· {shownWeeks.length} Wednesdays · {monthGroups.length} months · {filteredUsers.length} people</span>
                    </h3>
                    <Legend />
                </div>

                {loading ? (
                    <div className="p-5 space-y-3">
                        {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-8" />)}
                    </div>
                ) : grouped.length === 0 ? (
                    <div className="p-10">
                        <EmptyState
                            icon={GraduationCap}
                            title={visibleUsers.length ? "No one matches these filters" : "No employees in scope"}
                            description="Add employees from Control Panel to start marking weekly KNO attendance."
                            testid="kno-empty-state"
                        />
                    </div>
                ) : (
                    <div className="overflow-x-auto max-h-[70vh] overflow-y-auto scrollbar-thin">
                        <table className="w-full text-sm border-collapse" data-testid="kno-grid">
                            <thead className="bg-slate-50 sticky top-0 z-10 shadow-[0_1px_0_0_#e2e8f0]">
                                {/* Month band row */}
                                <tr className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                                    <th rowSpan={2} className="text-left px-4 py-3 sticky left-0 bg-slate-50 z-20 align-bottom border-r-2 border-slate-300" style={{ minWidth: 200 }}>Employee</th>
                                    {monthGroups.map((g, idx) => {
                                        const todayMonth = today.slice(0, 7);
                                        const isFutureMonth = g.ym > todayMonth;
                                        const isCurrentMonth = g.ym === todayMonth;
                                        return (
                                            <th
                                                key={g.ym}
                                                colSpan={g.weeks.length + 1}
                                                className={`px-2 py-2 text-center border-b border-slate-200 ${idx > 0 ? "border-l-2 border-l-slate-300" : ""} ${isFutureMonth ? "text-slate-400 bg-slate-50" : "text-[#25908A] bg-teal-50/60"}`}
                                                data-testid={`kno-month-${g.ym}`}
                                            >
                                                {formatMonth(g.ym)}
                                                {isCurrentMonth && <span className="ml-1.5 text-[9px] tracking-normal font-medium text-[#2EB4AC] normal-case">· current</span>}
                                            </th>
                                        );
                                    })}
                                </tr>
                                {/* Day row */}
                                <tr className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                                    {monthGroups.map((g) => (
                                    <React.Fragment key={g.ym}>
                                    {g.weeks.map((w) => {
                                        const isFuture = w > today;
                                        const isToday = w === today;
                                        const startsMonth = monthStartWeeks.has(w);
                                        return (
                                            <th key={w} className={`px-2 py-3 text-center ${startsMonth ? "border-l-2 border-l-slate-300" : ""} ${isFuture ? "text-slate-400" : ""}`} style={{ minWidth: 62 }}>
                                                <div className={`font-semibold ${isToday ? "text-[#2EB4AC]" : isFuture ? "text-slate-400" : "text-slate-700"}`}>
                                                    {formatDay(w)}
                                                    {isToday && <span className="ml-1 text-[9px] tracking-normal font-medium text-[#2EB4AC]">· today</span>}
                                                </div>
                                                <div className="text-[9px] text-slate-400 normal-case tracking-normal">Wed</div>
                                            </th>
                                        );
                                    })}
                                    <th className="px-2 py-3 text-center bg-rose-50/60 text-rose-700" style={{ minWidth: 64 }} title="Sessions missed (absent) this month. More than 1 is a critical instance." data-testid={`kno-missed-head-${g.ym}`}>
                                        <div className="font-semibold">Missed</div>
                                        <div className="text-[9px] normal-case tracking-normal text-rose-400">critical &gt;1</div>
                                    </th>
                                    </React.Fragment>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {grouped.map(({ dept, list }) => (
                                    <React.Fragment key={dept}>
                                        <tr className="bg-teal-50/40">
                                            <td colSpan={shownWeeks.length + monthGroups.length + 1} className="px-4 py-2 border-t border-b border-teal-100">
                                                <div className="flex items-center gap-2">
                                                    <Building2 className="h-4 w-4 text-[#25908A]" />
                                                    <span className="font-semibold text-slate-900">{dept}</span>
                                                    <span className="text-xs text-slate-500">· {list.length} {list.length === 1 ? "person" : "people"}</span>
                                                </div>
                                            </td>
                                        </tr>
                                        {list.map((u) => (
                                            <tr key={u.user_id} className="border-b border-slate-100 hover:bg-slate-50/60">
                                                <td className="px-4 py-2 sticky left-0 bg-white hover:bg-slate-50/60 font-medium text-slate-900 border-r-2 border-slate-300" style={{ minWidth: 200 }}>
                                                    <div className="flex items-center gap-2">
                                                        <div className="h-6 w-6 shrink-0 rounded-full bg-teal-50 text-[#25908A] border border-teal-100 flex items-center justify-center text-[10px] font-bold uppercase">
                                                            {(u.name || "?").charAt(0)}
                                                        </div>
                                                        <span className="truncate" style={{ maxWidth: 140 }}>{u.name}</span>
                                                        {criticalIds.has(u.user_id) && <AlertTriangle className="h-3.5 w-3.5 text-rose-500 shrink-0" title="Critical: missed more than 1 session in a month" />}
                                                    </div>
                                                </td>
                                                {monthGroups.map((g) => (
                                                <React.Fragment key={g.ym}>
                                                {g.weeks.map((w) => {
                                                    const rec = map.get(u.user_id)?.[w];
                                                    const status = rec?.status || "unmarked";
                                                    const isFuture = w > today;
                                                    const isToday = w === today;
                                                    const startsMonth = monthStartWeeks.has(w);
                                                    // Show the ⓘ evidence dot only on non-future,
                                                    // non-unmarked cells so we don't clutter empty cells.
                                                    const showEvidenceDot = !isFuture && status !== "unmarked";
                                                    return (
                                                        <td key={w} className={`px-2 py-2 text-center relative ${startsMonth ? "border-l-2 border-l-slate-300" : ""} ${isToday ? "bg-teal-50/40" : isFuture ? "bg-slate-50/40" : ""}`}>
                                                            <div className="relative inline-block">
                                                                <Cell status={status} onClick={() => cycleStatus(u, w, status)} disabled={!canMark || isFuture} future={isFuture} testid={`kno-cell-${u.user_id}-${w}`} />
                                                                {showEvidenceDot && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={(e) => { e.stopPropagation(); openEvidence(u, w); }}
                                                                        title="Why was this marked?"
                                                                        aria-label="Show evidence"
                                                                        data-testid={`kno-evidence-${u.user_id}-${w}`}
                                                                        className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-white ring-1 ring-slate-300 text-slate-500 hover:text-teal-700 hover:ring-teal-500 hover:bg-teal-50 inline-flex items-center justify-center shadow-sm transition"
                                                                    >
                                                                        <Info className="h-2.5 w-2.5" />
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </td>
                                                    );
                                                })}
                                                {(() => {
                                                    const n = missedOf(u.user_id, g.ym);
                                                    return (
                                                        <td className="px-2 py-2 text-center bg-rose-50/30" data-testid={`kno-missed-${u.user_id}-${g.ym}`}>
                                                            <span className={`inline-flex items-center justify-center min-w-[28px] h-6 px-1.5 rounded-full text-xs font-bold tabular-nums border ${n > 1 ? "bg-rose-600 text-white border-rose-600" : n === 1 ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-white text-slate-400 border-slate-200"}`}>
                                                                {n}
                                                            </span>
                                                        </td>
                                                    );
                                                })()}
                                                </React.Fragment>
                                                ))}
                                            </tr>
                                        ))}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
            {evidence && (
                <EvidenceModal
                    userName={evidence.userName}
                    sessionDate={evidence.sessionDate}
                    loading={evidence.loading}
                    data={evidence.data}
                    error={evidence.error}
                    onClose={() => setEvidence(null)}
                />
            )}
        </div>
    );
};

const EvidenceModal = ({ userName, sessionDate, loading, data, error, onClose }) => {
    const pretty = sessionDate ? new Date(`${sessionDate}T00:00:00`).toLocaleDateString("en-IN", { weekday: "long", day: "2-digit", month: "short", year: "numeric" }) : "";
    const fmtTs = (ts) => { try { return new Date(ts).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };
    const statusPill = (s) => {
        if (s === "present") return <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200">Present</span>;
        if (s === "absent") return <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-rose-50 text-rose-700 ring-1 ring-rose-200">Absent</span>;
        if (s === "cancelled") return <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-slate-100 text-slate-600 ring-1 ring-slate-200">Cancelled</span>;
        return <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-slate-50 text-slate-500 ring-1 ring-slate-200">Unmarked</span>;
    };
    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center px-4" data-testid="kno-evidence-modal">
            <button aria-label="Close" onClick={onClose} className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" />
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden ring-1 ring-slate-200">
                <div className="px-5 py-4 border-b border-slate-100 flex items-start justify-between gap-3">
                    <div>
                        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">KNO Evidence</div>
                        <div className="text-base font-semibold text-slate-900 mt-0.5">{userName}</div>
                        <div className="text-xs text-slate-500">{pretty}</div>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-700" aria-label="Close" data-testid="kno-evidence-close">✕</button>
                </div>
                <div className="p-5 max-h-[70vh] overflow-y-auto text-sm">
                    {loading && <div className="text-slate-500">Loading evidence…</div>}
                    {error && <div className="text-rose-600">{error}</div>}
                    {data && !loading && (
                        <div className="space-y-4">
                            <div className="flex items-center gap-2">
                                {statusPill(data.status)}
                                <span className="text-xs text-slate-500">by <strong className="text-slate-700">{data.marked_by || "—"}</strong></span>
                            </div>
                            <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2.5 text-[13px] text-slate-700 leading-relaxed">
                                {data.reason}
                            </div>
                            {data.final_in && (
                                <div>
                                    <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700 mb-1">Office check-in message</div>
                                    <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2.5">
                                        <div className="text-[13px] text-slate-800 whitespace-pre-wrap">{data.final_in.msg || "—"}</div>
                                        <div className="text-[11px] text-slate-500 mt-1">
                                            {fmtTs(data.final_in.ts)} · <span className="font-semibold uppercase tracking-wider">{data.final_in.kind}</span>
                                            {typeof data.final_in.mins === "number" && (<> · {String(Math.floor(data.final_in.mins / 60)).padStart(2, "0")}:{String(data.final_in.mins % 60).padStart(2, "0")}</>)}
                                        </div>
                                    </div>
                                </div>
                            )}
                            {data.is_wfh && data.has_log && (
                                <div>
                                    <div className="text-[10px] font-bold uppercase tracking-widest text-amber-700 mb-1">WFH signal in messages</div>
                                    <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5 text-[13px] text-slate-800">
                                        A WFH note was detected in this day's chat — that's why KNO auto-marked absent.
                                    </div>
                                </div>
                            )}
                            {data.in_candidates && data.in_candidates.length > 0 && (
                                <div>
                                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Other IN candidates that day</div>
                                    <ul className="space-y-1.5">
                                        {data.in_candidates.slice(0, 6).map((c, i) => (
                                            <li key={i} className="rounded-md bg-white border border-slate-200 px-2.5 py-1.5 text-[12.5px] text-slate-700">
                                                <div className="text-slate-800">{c.msg}</div>
                                                <div className="text-[10.5px] text-slate-500 mt-0.5">{fmtTs(c.ts)} · <span className="uppercase tracking-wider font-semibold">{c.kind}</span></div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {!data.has_log && data.marked_by === "system" && (
                                <div className="rounded-lg bg-slate-50 border border-dashed border-slate-300 px-3 py-2.5 text-[12.5px] text-slate-600">
                                    No WhatsApp attendance messages were archived for this Wednesday. The auto-mark defaulted to <strong>{data.status}</strong> per the sync rule.
                                </div>
                            )}
                            {data.marked_by && data.marked_by !== "system" && (
                                <div className="rounded-lg bg-teal-50 border border-teal-200 px-3 py-2.5 text-[12.5px] text-teal-800">
                                    This row was set manually. Auto-sync will never overwrite it.
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const Cell = ({ status, onClick, disabled, future, testid }) => {
    const base = "h-8 w-8 rounded-md inline-flex items-center justify-center transition-all";
    if (future) {
        return (
            <span data-testid={testid} aria-label="Future Wednesday — not yet markable"
                className={`${base} text-slate-300 select-none cursor-not-allowed`}>
                ·
            </span>
        );
    }
    if (status === "present") {
        return (
            <button onClick={onClick} disabled={disabled} data-testid={testid}
                className={`${base} bg-green-100 text-green-700 hover:bg-green-200 ${disabled ? "cursor-not-allowed" : ""}`}
                aria-label="Present — click to toggle">
                <CheckCircle2 className="h-4 w-4" />
            </button>
        );
    }
    if (status === "absent") {
        return (
            <button onClick={onClick} disabled={disabled} data-testid={testid}
                className={`${base} bg-red-100 text-red-700 hover:bg-red-200 ${disabled ? "cursor-not-allowed" : ""}`}
                aria-label="Absent — click to toggle">
                <XCircle className="h-4 w-4" />
            </button>
        );
    }
    if (status === "cancelled") {
        return (
            <button onClick={onClick} disabled={disabled} data-testid={testid}
                className={`${base} bg-slate-100 text-slate-500 hover:bg-slate-200 ${disabled ? "cursor-not-allowed" : ""}`}
                aria-label="Session cancelled — click to toggle">
                <Ban className="h-4 w-4" />
            </button>
        );
    }
    return (
        <button onClick={onClick} disabled={disabled} data-testid={testid}
            className={`${base} bg-slate-50 text-slate-300 border border-dashed border-slate-200 hover:border-[#2EB4AC] hover:text-[#2EB4AC] ${disabled ? "cursor-not-allowed" : ""}`}
            aria-label="Unmarked — click to mark">
            <MinusCircle className="h-4 w-4" />
        </button>
    );
};

const StatTile = ({ icon: Icon, label, value, color, testid }) => (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-4 sm:p-6" data-testid={testid}>
        <div className="flex items-start justify-between">
            <div>
                <div className="text-[10px] sm:text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
                <div className="mt-1 sm:mt-2 font-heading text-2xl sm:text-3xl font-bold text-slate-900 tabular-nums">{value}</div>
            </div>
            <div className={`h-8 w-8 sm:h-10 sm:w-10 rounded-lg flex items-center justify-center ${color}`}>
                <Icon className="h-4 w-4" />
            </div>
        </div>
    </div>
);

const Legend = () => (
    <div className="inline-flex items-center gap-3 text-xs text-slate-500 flex-wrap">
        <span className="inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5 text-green-600" /> Present</span>
        <span className="inline-flex items-center gap-1"><XCircle className="h-3.5 w-3.5 text-red-600" /> Absent</span>
        <span className="inline-flex items-center gap-1"><Ban className="h-3.5 w-3.5 text-slate-500" /> Cancelled</span>
        <span className="inline-flex items-center gap-1"><MinusCircle className="h-3.5 w-3.5 text-slate-400" /> Unmarked</span>
    </div>
);

export default KnoAttendance;
